/* eslint-disable consistent-return */
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

interface Props {
  elementQuerySelector: string;
  nextUrl?: string;
  prevUrl?: string;
  confirmTimeout?: number; // how long the "armed" state lasts
  wheelEndTimeout?: number; // ms without wheel events = new gesture
  cooldownAfterNavigate?: number; // ms to block re-navigation after success
}

type ReadyState = null | 'top' | 'bottom' | 'both';

const THROTTLE_DELAY = 100;

const useNavigateScroll = ({
  elementQuerySelector,
  nextUrl,
  prevUrl,
  confirmTimeout = 3000,
  wheelEndTimeout = 50,
  cooldownAfterNavigate = 0,
}: Props) => {
  const navigate = useNavigate();

  const readyRef = useRef<ReadyState>(null);
  const expiryTimerRef = useRef<number | null>(null);
  const lastScrollCallRef = useRef<number>(0);

  // Gesture segmentation refs
  const lastWheelTimeRef = useRef<number>(0);
  const inWheelGestureRef = useRef(false);
  const wheelEndTimerRef = useRef<number | null>(null);

  // Cooldown state
  const cooldownRef = useRef(false);
  const cooldownTimerRef = useRef<number | null>(null);

  const handleNavigateManual = (direction: 'prev' | 'next') => {
    if (direction === 'next' && nextUrl) navigate(nextUrl);
    if (direction === 'prev' && prevUrl) navigate(prevUrl);
  };

  /** Arm navigation at top/bottom and set timers */
  const armNavigation = (pos: ReadyState) => {
    readyRef.current = pos;
    // auto-expire after confirmTimeout
    if (expiryTimerRef.current) clearTimeout(expiryTimerRef.current);
    expiryTimerRef.current = window.setTimeout(() => {
      readyRef.current = null;
      expiryTimerRef.current = null;
    }, confirmTimeout);
  };

  // Set arm to top after navigate for user can navigate again before confirmTimeout expired
  useEffect(() => {
    const container = document.querySelector(elementQuerySelector) as HTMLElement | null;
    if (!container) return;
    /** Reset navigation state */
    readyRef.current = null;

    container.scrollTop = 0;
    armNavigation('both');

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prevUrl, nextUrl]);

  useEffect(() => {
    const container = document.querySelector(elementQuerySelector) as HTMLElement | null;
    if (!container) return;

    const performNavigate = (url: string) => {
      if (!url) return;
      if (cooldownRef.current) return; // block if cooling down

      if (cooldownAfterNavigate > 0) {
        cooldownRef.current = true;
        if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current);
        cooldownTimerRef.current = window.setTimeout(() => {
          cooldownRef.current = false;
          cooldownTimerRef.current = null;
        }, cooldownAfterNavigate);
      }

      navigate(url);
      readyRef.current = null;
    };

    const handleScroll = (e: Event) => {
      const now = Date.now();
      const target = e.target as HTMLElement;
      const { scrollTop, scrollHeight, clientHeight } = target;

      // short page = no scroll possible
      if (scrollHeight <= clientHeight) return;

      const atTop = scrollTop <= 0;
      const atBottom = scrollTop + clientHeight >= scrollHeight;

      // Always evaluate if at a boundary
      if (atTop) {
        if (readyRef.current !== 'top') armNavigation('top');
        lastScrollCallRef.current = now; // reset throttle
        return;
      }
      if (atBottom) {
        if (readyRef.current !== 'bottom') armNavigation('bottom');
        lastScrollCallRef.current = now;
        return;
      }

      // Throttle middle scrolling for performance
      if (now - lastScrollCallRef.current < THROTTLE_DELAY) return;
      lastScrollCallRef.current = now;

      if (readyRef.current) {
        readyRef.current = null;
        if (expiryTimerRef.current) {
          clearTimeout(expiryTimerRef.current);
          expiryTimerRef.current = null;
        }
      }
    };

    const handleWheel = (e: WheelEvent) => {
      const target = e.currentTarget as HTMLElement;
      const { scrollHeight, clientHeight } = target;
      const shortPage = scrollHeight <= clientHeight;

      const now = Date.now();

      // If enough time has passed, this is a new gesture
      if (now - lastWheelTimeRef.current > wheelEndTimeout) {
        inWheelGestureRef.current = false;
      }
      lastWheelTimeRef.current = now;

      // Reset "gesture end" timer
      if (wheelEndTimerRef.current) clearTimeout(wheelEndTimerRef.current);
      wheelEndTimerRef.current = window.setTimeout(() => {
        inWheelGestureRef.current = false;
        wheelEndTimerRef.current = null;
      }, wheelEndTimeout);

      // Only first wheel in gesture can trigger
      if (!inWheelGestureRef.current) {
        inWheelGestureRef.current = true;

        if (shortPage) {
          if (e.deltaY < 0) {
            if (readyRef.current && ['top', 'both'].includes(readyRef.current) && prevUrl) {
              performNavigate(prevUrl);
            } else {
              armNavigation('top');
            }
          }
          if (e.deltaY > 0) {
            if (readyRef.current && ['bottom', 'both'].includes(readyRef.current) && nextUrl) {
              performNavigate(nextUrl);
            } else {
              armNavigation('bottom');
            }
          }
        } else {
          if (readyRef.current === 'top' && e.deltaY < 0 && prevUrl) {
            performNavigate(prevUrl);
          }
          if (readyRef.current === 'bottom' && e.deltaY > 0 && nextUrl) {
            performNavigate(nextUrl);
          }
        }
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    container.addEventListener('wheel', handleWheel, { passive: true });

    return () => {
      container.removeEventListener('scroll', handleScroll);
      container.removeEventListener('wheel', handleWheel);
      if (expiryTimerRef.current) clearTimeout(expiryTimerRef.current);
      if (wheelEndTimerRef.current) clearTimeout(wheelEndTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elementQuerySelector,
    prevUrl,
    nextUrl,
    confirmTimeout,
    wheelEndTimeout,
    cooldownAfterNavigate]);

  return {
    goPrev: () => handleNavigateManual('prev'),
    goNext: () => handleNavigateManual('next'),
  };
};

export default useNavigateScroll;
